import { Fragment, useEffect } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';

import { FaTrash } from 'react-icons/fa';
import { IoIosArrowBack, IoMdArchive } from 'react-icons/io';
import { MdEdit } from 'react-icons/md';
import { RiFileCopyFill } from 'react-icons/ri';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Layout, Modal } from '~/components';
import { PowerCreator } from '~/models/PowerCreator';
import api from '~/services/api';
import { formatHMS, getSeconds } from '~/utils';

import {
  Header,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  Container,
  HeaderButtons,
  Section,
  Divider,
  SerieContainer,
  SerieSection,
  DeleteContainer,
} from './styles';

type IRouteParams = {
  id: string;
};

function MetLibraryDetails() {
  const { goBack, location, push } = useHistory();
  const { id } = useParams<IRouteParams>();

  const [data, setData] = useState<PowerCreator>(
    location.state as PowerCreator,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const restTime = formatHMS(
    getSeconds(data.total_time ?? '0') - getSeconds(data.total_action ?? '0'),
  );

  const loadLibrary = useCallback(async () => {
    const { data: apiData } = await api.get(`power-creators/${id}`);

    setData(apiData.data);
  }, [id]);

  useEffect(() => {
    !data && loadLibrary();

    console.log(`data`, data);
  }, [data, loadLibrary]);

  const series: { [key: string]: Array<number> } = {
    1: [0],
    2: [0, 1],
    3: [0, 1, 2],
    4: [0, 1, 2, 3],
    5: [0, 1, 2, 3, 4],
    6: [0, 1, 2, 3, 4, 5],
    7: [0, 1, 2, 3, 4, 5, 6],
    8: [0, 1, 2, 3, 4, 5, 6, 7],
    9: [0, 1, 2, 3, 4, 5, 6, 7, 8],
    10: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  };

  function DeleteModal() {
    return (
      <Modal isOpen={showDeleteModal} setOpen={setShowDeleteModal}>
        <DeleteContainer>
          <h1>Atenção</h1>
          <p>
            Essa função excluirá o método:
            <br />
            <strong>{data.tranning_method}</strong>.
            <br />
            <br />
            Tem certeza que deseja continuar?
          </p>

          <div>
            <button type="button" onClick={deleteLibrary}>
              Sim
            </button>
            <button type="button" onClick={() => setShowDeleteModal(false)}>
              Não
            </button>
          </div>
        </DeleteContainer>
      </Modal>
    );
  }

  const deleteLibrary = useCallback(async () => {
    try {
      await api.delete(`power-creators/${id}`);

      toast.success(`Método deletado com sucesso!`);

      goBack();
    } catch (err) {
      toast.error(`Ocorreu um erro ao tentar deletar o método!`);
    }
  }, [id, goBack]);

  const archiveLibrary = useCallback(async () => {
    try {
      await api.post(`power-creators/${id}`, {
        action: 'archive',
      });

      toast.success(`Método arquivado com sucesso!`);

      goBack();
    } catch (err) {
      toast.error(`Ocorreu um erro ao tentar arquivado o método!`);
    }
  }, [id, goBack]);

  const duplicateLibrary = useCallback(async () => {
    try {
      await api.post(`power-creators`, data);

      toast.success(`Método duplicado com sucesso!`);

      goBack();
    } catch (err) {
      toast.error(`Ocorreu um erro ao tentar duplicar o método!`);
    }
  }, [data, goBack]);

  const handleNavigateToEditTraining = useCallback(() => {
    push(`/professional/lab/power-creator/${data.id}/edit`, data);
  }, [push, data]);

  return (
    <Layout title="I.LAB">
      <DeleteModal />

      <Header>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack color="#FFDF22" size={24} />
          <PageHeaderBackButtonLabel>MET - LIBRARY</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <HeaderButtons>
          <button type="button" onClick={handleNavigateToEditTraining}>
            <MdEdit color="#000" size="19" />
          </button>

          <button type="button" onClick={archiveLibrary}>
            <IoMdArchive color="#000" size="19" />
          </button>

          <button onClick={duplicateLibrary} type="button">
            <RiFileCopyFill color="#000" size="19" />
          </button>

          <button onClick={() => setShowDeleteModal(true)} type="button">
            <FaTrash color="#000" size="19" />
          </button>
        </HeaderButtons>
      </Header>

      <Container>
        <Section>
          <div>
            <strong>Método:&nbsp;</strong>
            <br />
            {data.tranning_method}
          </div>

          <div>
            <strong>Princípio ativo:&nbsp;</strong>
            <br />
            {data.method_principle}
          </div>

          <div>
            <strong>Execução:&nbsp;</strong>
            <br />
            {data.execution}
          </div>

          <div>
            <strong>Descrição Técnica:&nbsp;</strong>
            <br />
            {data.technical_description}
          </div>

          <div>
            <strong>Intervalo de recuperação (entre séries):&nbsp;</strong>
            <br />
            {restTime}
          </div>

          <div>
            <strong>Número de séries:&nbsp;</strong>
            <br />
            {data.series_number}
          </div>
        </Section>

        <Divider />

        <SerieContainer>
          {series[data.series_number].map((serie) => (
            <Fragment key={serie}>
              <SerieSection>
                <div>
                  <strong>Série {serie + 1} - execução:&nbsp;</strong>
                  <br />
                  {Object.values(data.series_names)[serie]}
                </div>

                <div>
                  <strong>Número de repetições:&nbsp;</strong>
                  <br />
                  {Object.values(data.series_number_of_repetitions)[serie]}
                </div>

                <div>
                  <strong>Tempo de ação:&nbsp;</strong>
                  <br />
                  {Object.values(data.series_actions_time)[serie]}
                </div>

                {data.series_number > 0 && serie < data.series_number - 1 && (
                  <div>
                    <strong>
                      Intervalo de recuperação (entre séries):&nbsp;
                    </strong>

                    <br />
                    {Object.values(data.series_recovery_Time)[serie]}
                  </div>
                )}
              </SerieSection>

              <Divider />
            </Fragment>
          ))}
        </SerieContainer>

        <Section>
          <div>
            <strong>Volume Nº repetições:&nbsp;</strong>
            <br />
            {data.volume_total}
          </div>

          <div>
            <strong>Tempo de ação Total:&nbsp;</strong>
            <br />
            {data.total_action}
          </div>

          <div>
            <strong>
              Intervalo de recuperação total (entre séries):&nbsp;
            </strong>
            <br />
            {restTime}
          </div>

          <div>
            <strong>Tempo Total:&nbsp;</strong>
            <br />
            {data.total_time}
          </div>
        </Section>
      </Container>
    </Layout>
  );
}

export default MetLibraryDetails;
